import React, { useEffect, useMemo, useState } from 'react';
import Highcharts from 'highcharts';
import { useTranslate } from "../../../../hooks/LanguageProvider";
import variablePie from 'highcharts/modules/variable-pie';
import HighchartsReact from 'highcharts-react-official';
import { MoonLoader } from "react-spinners";

variablePie(Highcharts);

interface Options {
  DrivingValue: number;
  ParkingValue: number;
  ParkingEngineRunningValue: number;
  LastTransmissionValue: number;
}

type FleetStateProps = {
  options: Options;
  title?: string;
  refreshSeconds?: number;
};

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const FleetSate: React.FC<FleetStateProps> = ({ options, title = "Apercu de votre flotte", refreshSeconds = 60 }) => {

  const { translate } = useTranslate();
  const [values, setValues] = useState(options);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchFleetState = async () => {
    const userId = localStorage.getItem("userid");
    if (!backendUrl || !userId) {
      setValues(options);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${backendUrl}/api/map/find/${userId}`, { mode: "cors" });
      if (!response.ok) throw new Error(`Map API ${response.status}`);
      const data = await response.json();
      const vehicles = Array.isArray(data) ? data : [];
      setValues({
        DrivingValue: vehicles.filter((vehicle: any) => Number(vehicle?.ENGINESTAT) === 1 && Number(vehicle?.SOG) > 5).length,
        ParkingValue: vehicles.filter((vehicle: any) => Number(vehicle?.ENGINESTAT) === 0).length,
        ParkingEngineRunningValue: vehicles.filter((vehicle: any) => Number(vehicle?.ENGINESTAT) === 1 && Number(vehicle?.SOG) <= 5).length,
        LastTransmissionValue: options.LastTransmissionValue || 0,
      });
    } catch (error: any) {
      setError(error.message || "Erreur API map");
      setValues(options);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFleetState();
    const interval = window.setInterval(fetchFleetState, Math.max(15, Number(refreshSeconds) || 60) * 1000);
    return () => window.clearInterval(interval);
  }, [refreshSeconds, options.DrivingValue, options.ParkingValue, options.ParkingEngineRunningValue, options.LastTransmissionValue]);

  const total = values.DrivingValue + values.ParkingValue + values.ParkingEngineRunningValue;
  const chartOptions: Highcharts.Options = useMemo(() => ({
    chart: {
      type: 'variablepie',
      height: 285,
      backgroundColor: 'transparent',
      spacing: [0, 0, 0, 0],
    },
    credits: { enabled: false },
    title: {
      text: undefined,
    },
    tooltip: {
      headerFormat: '',
      pointFormat:
        '<span style="color:{point.color}">\u25CF</span> <b> {point.name}</b><br/>' +
        '<b>{point.y}</b> Vehicles<br>' ,
    },
    
    plotOptions: {
      series: {
        allowPointSelect: true,
        cursor: 'pointer',
        dataLabels: [{
          enabled: true,
          distance: 5
        },
        
        {
          enabled: true,
          distance: -100,
          format: '{point.percentage:.1f}%',
          style: {
            fontSize: '16px',
            textOutline: 'none',
            opacity: 0.7
          },
          filter: {
            operator: '>',
            property: 'percentage',
            value: 10
          }
        }]
      }
    },
    series: [
      {
        type: 'variablepie',
        minPointSize: 10,
        innerSize: '15%',
        zMin: 0,
        name: 'categories',
        borderRadius: 5,
        data: [
          { name: translate("Driving"), y: values.DrivingValue, z: 100 },
          { name: translate("Parking"), y: values.ParkingValue, z: 100 },
          { name: translate("Stop with engine start"), y: values.ParkingEngineRunningValue, z: 100 },
        ],
        colors: [
          '#00e272',
          '#2caffe',
          '#d7300c',
        //  '#343635',
        ],
        connectorWidth: 1,
        connectorColor: 'black',
      },
    ],
  }), [values, translate]);

  return (
    <div className="dash2-chart-widget dash2-fleet-state-widget">
      <div className="dash2-chart-header">
        <div>
          <span className="dash2-chart-kicker">Map temps reel</span>
          <h3>{title || translate('Overview of your fleet')}</h3>
        </div>
        <button type="button" className="dash2-chart-refresh" onClick={fetchFleetState} title={translate("Refresh")}>
          <i className="las la-sync" />
        </button>
      </div>
      <div className="dash2-chart-metrics">
        <span><strong>{total}</strong>Total</span>
        <span><strong>{values.DrivingValue}</strong>{translate("Driving")}</span>
        <span><strong>{values.ParkingValue}</strong>{translate("Parking")}</span>
      </div>
      {loading && (
        <div className="dash2-chart-loader">
          <MoonLoader size={34} color="#2563eb" loading={loading} />
        </div>
      )}
      {error && <div className="dash2-chart-empty">{error}</div>}
      {!loading && !error && <HighchartsReact highcharts={Highcharts} options={chartOptions} />}
    </div>
  );
};

export default FleetSate;
